import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, EMPTY, firstValueFrom, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { ROUTING_PATH } from '@shared/constants/routingPath';
import { GqlService, UserTaskType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';

import { UserTasksStore } from './user-tasks.store';

export const MAP_PATH_BY_TASK_TYPE = new Map<
  UserTaskType,
  (taskId: string, vendorId: string) => string
>([
  [
    UserTaskType.CHANGE_ORDER,
    (taskId: string) =>
      `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}/${taskId}`,
  ],
  [
    UserTaskType.FORECAST_VENDOR_COSTS,
    (_, vendorId) =>
      `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}?vendor_id=${vendorId}`,
  ],
  [
    UserTaskType.PERIOD_CLOSE,
    () => `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`,
  ],
  [
    UserTaskType.VENDOR_ESTIMATE,
    () => `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`,
  ],
]);

@Injectable({ providedIn: 'root' })
export class UserTasksService {
  private authQuery = inject(AuthQuery);
  private gqlService = inject(GqlService);
  private ld = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private userTasksStore = inject(UserTasksStore);

  showInvoiceDetailLink$: Observable<boolean>;
  userTasksIsEnabled = false;
  userTasksIsEnabled$: Observable<boolean>;

  constructor() {
    this.showInvoiceDetailLink$ = this.ld.select$((flags) => !!flags.invoice_detail);

    this.userTasksIsEnabled$ = this.ld.select$((flags) => !!flags.section_task_list);

    this.showInvoiceDetailLink$.pipe(takeUntilDestroyed()).subscribe((enabled) => {
      MAP_PATH_BY_TASK_TYPE.set(
        UserTaskType.INVOICE,
        (taskId: string) =>
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}${
            enabled ? `/${taskId.replace('Categorize Invoice| ', '')}` : ''
          }`
      );
    });

    this.userTasksIsEnabled$.pipe(takeUntilDestroyed()).subscribe((enabled) => {
      this.userTasksIsEnabled = enabled;
    });
  }

  getUserTaskList$() {
    return this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          this.userTasksStore.setLoading(true);

          const userId = this.authQuery.getValue();

          if (userId.trial_id && this.userTasksIsEnabled) {
            return combineLatest([this.gqlService.listUserTasks$()]);
          }

          return EMPTY;
        })
      )
      .pipe(
        tap(([{ data, errors }]) => {
          this.userTasksStore.set(data || []);

          if (errors) {
            this.overlayService.error(errors);
          }

          this.userTasksStore.setLoading(false);
        })
      );
  }

  async triggerUserTaskList$() {
    if (this.userTasksIsEnabled) {
      this.userTasksStore.setLoading(true);
      const { data, errors, success } = await firstValueFrom(this.gqlService.listUserTasks$());

      if (success && data) {
        this.userTasksStore.set(data || []);
      }

      if (errors) {
        this.overlayService.error(errors);
      }

      this.userTasksStore.setLoading(false);
    }
  }
}
